import { CATEGORY_GROUP_MEMBERS } from './config'
import { DetectionResult } from './get_detections'

// Client-side result filters. These can't be pushed to the backend (get_detections has no
// image-presence filter, and "error event" is a derived category set), so they're applied to
// accumulated pages — see useFilteredDetections.
export type ClientFilters = {
    // Per-detection: keep only detections that carry at least one image. Applies in every view.
    withImagesOnly: boolean
    // Group-by-LP only: keep only plate groups with at least one image / at least one error event.
    lpWithImage: boolean
    lpWithError: boolean
}

export const INITIAL_CLIENT_FILTERS: ClientFilters = {
    withImagesOnly: false,
    lpWithImage: false,
    lpWithError: false
}

// A detection "has an image" when any of its three artifacts has a resolved URL (matches the
// export's exportHasImages check).
export const detectionHasImage = (d: DetectionResult): boolean =>
    Boolean(d.images.render.url || d.images.roi.url || d.images.lp.url)

// An "error event" is one of the four ER-affecting categories (the old Lot Analysis Tool's
// error_event_config), which live as the Error Rate group's members.
export const detectionIsErrorEvent = (d: DetectionResult): boolean =>
    CATEGORY_GROUP_MEMBERS.error_rate.includes(d.category)

// The lp* filters only mean anything in the Group-by-LP view, so they don't count elsewhere.
export const anyClientFilterActive = (filters: ClientFilters, isGroupByLP: boolean): boolean =>
    filters.withImagesOnly || (isGroupByLP && (filters.lpWithImage || filters.lpWithError))

// A "unit" of a display page: a single detection (flat) or a whole plate group's detections
// (Group-by-LP). The accumulator paginates by unit.
export type Unit = DetectionResult[]

// Flat (List/Gallery): each detection is its own unit; `withImagesOnly` drops imageless detections.
export const buildFlatUnits = (rows: DetectionResult[], filters: ClientFilters): Unit[] => {
    const matches = filters.withImagesOnly ? rows.filter(detectionHasImage) : rows
    return matches.map((d) => [d])
}

// Group-by-LP: group rows by license plate, preserving order. The backend returns complete plate
// groups per page, so concatenating pages keeps each plate contiguous; the Map merges the rare
// cross-page split safely. Null plates are dropped (as the view already does). lp* filters keep/drop
// whole groups; `withImagesOnly` additionally hides imageless detections within a kept group.
export const buildGroupUnits = (rows: DetectionResult[], filters: ClientFilters): Unit[] => {
    const order: string[] = []
    const byPlate = new Map<string, DetectionResult[]>()
    for (const d of rows) {
        const plate = d.license_plate
        if (!plate) continue
        const existing = byPlate.get(plate)
        if (existing) existing.push(d)
        else {
            byPlate.set(plate, [d])
            order.push(plate)
        }
    }

    const units: Unit[] = []
    for (const plate of order) {
        const group = byPlate.get(plate) ?? []
        if (filters.lpWithError && !group.some(detectionIsErrorEvent)) continue
        if (filters.lpWithImage && !group.some(detectionHasImage)) continue
        const visible = filters.withImagesOnly ? group.filter(detectionHasImage) : group
        if (visible.length === 0) continue
        units.push(visible)
    }
    return units
}

// Distinct plates seen so far — the scanned-unit count in grouped mode (matches the server's `total`,
// which counts plates when grouped).
export const distinctPlateCount = (rows: DetectionResult[]): number =>
    new Set(rows.map((d) => d.license_plate).filter(Boolean)).size
